<template>
  <div class="form">
    <b-form @submit.prevent="onSubmit(mode)">
      <p class="form__title">
        {{ formTitle }}
      </p>
      <b-container fluid>
        <b-row class="form__row">
          <b-col :md="4">
            <!-- Basic user information -->
            <b-form-group id="input-group-1" label-for="input-1">
              <template #label>
                Nom <span class="required-marker" aria-hidden="true">*</span><span class="sr-only">requis</span>
              </template>
              <b-form-input id="input-1" v-model="form.nom" placeholder="Entrez votre nom" required
                            :disabled="mode === 'profile' || mode === 'reset'" />
            </b-form-group>
            <b-form-group id="input-group-2" label-for="input-2">
              <template #label>
                Prenom <span class="required-marker" aria-hidden="true">*</span><span class="sr-only">requis</span>
              </template>
              <b-form-input id="input-2" v-model="form.prenom" placeholder="Entrez votre prenom" required
                            :disabled="mode === 'profile' || mode === 'reset'" />
            </b-form-group>
            <b-form-group id="input-group-identifiant" label-for="input-identifiant">
              <template #label>
                Identifiant <span class="required-marker" aria-hidden="true">*</span><span class="sr-only">requis</span>
              </template>
              <b-form-input id="input-identifiant" v-model="form.identifiant" required
                            :disabled="mode === 'profile' || mode === 'reset'" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row class="form__row">
          <b-col :md="4">
            <!-- Additional information (visible when not in reset mode) -->
            <template v-if="mode !== 'reset'">
              <b-form-group id="input-group-4" label-for="input-4">
                <template #label>
                  Departement <span class="required-marker" aria-hidden="true">*</span><span class="sr-only">requis</span>
                </template>
                <b-form-input id="input-4" v-model="form.departement" placeholder="Entrez votre departement" required
                              :disabled="mode === 'profile' || mode === 'reset'" />
              </b-form-group>
              <b-form-group id="input-group-5" label-for="input-5">
                <template #label>
                  Role <span class="required-marker" aria-hidden="true">*</span><span class="sr-only">requis</span>
                </template>
                <b-form-select v-if="mode !== 'ajouter'" v-model="form.roles[0]" :options="roleList"
                               :disabled="mode === 'profile' || mode === 'reset'" />
                <b-form-select v-else v-model="roles" :options="roleList" required />
              </b-form-group>
              <b-form-group id="input-group-5" label="Enregistrement des appels" label-for="input-5">
                <b-form-select v-if="mode === 'modifier'" v-model="form.profile.callRecording"
                               :options="callRecording" />
                <b-form-select v-else v-model="form.callRecording" :options="callRecording"
                               :disabled="mode === 'profile' || mode === 'reset'" />
              </b-form-group>
              <b-form-group id="input-group-6" label-for="input-6">
                <template #label>
                  Telephone <span class="required-marker" aria-hidden="true">*</span><span class="sr-only">requis</span>
                </template>
                <b-form-input id="input-6" v-model="form.telephone" type="number" placeholder="Entrez votre telephone"
                              required :disabled="mode === 'profile' || mode === 'reset'" />
              </b-form-group>
              <b-form-group id="input-group-7" label-for="input-7">
                <template #label>
                  Email <span class="required-marker" aria-hidden="true">*</span><span class="sr-only">requis</span>
                </template>
                <b-form-input id="input-7" v-model="form.email" type="email" placeholder="Enter email" required
                              :disabled="mode === 'profile' || mode === 'reset'" />
              </b-form-group>
            </template>

            <div v-if="mode === 'ajouter'" class="activation-notice" role="status" aria-live="polite">
              <feather-icon icon="MailIcon" size="20" class="activation-notice__icon" aria-hidden="true" />
              <div>
                <p class="activation-notice__title">
                  Invitation envoyée automatiquement
                </p>
                <p class="activation-notice__message">
                  L'utilisateur définira son mot de passe depuis le lien reçu par e-mail.
                </p>
              </div>
            </div>

            <!-- Password fields -->
            <template v-if="showPasswordFields || mode === 'reset'">
              <!-- Current password (only for profile mode) -->
              <b-form-group v-if="mode === 'profile'" id="input-group-8" label="Mot de passe actuel"
                            label-for="input-8">
                <b-form-input id="input-8" v-model="form.currentPassword" type="password"
                              placeholder="Entrez votre mot de passe actuel" required />
              </b-form-group>

              <!-- New password fields -->
              <template v-if="mode === 'profile' || mode === 'reset'">
                <b-form-group id="input-group-9" :label="mode === 'profile' ? 'Nouveau mot de passe' : 'Mot de passe'"
                              label-for="input-9">
                  <b-form-input id="input-9" v-model="form.password" type="password"
                                placeholder="Entrez votre mot de passe" required @input="validatePassword" />
                </b-form-group>

                <!-- Password requirements display -->
                <div v-if="form.password" class="password-requirements mb-3">
                  <div class="d-flex align-items-center mb-2">
                    <b-progress :value="passwordCompliance" height="10px" :variant="strengthVariant"
                                class="flex-grow-1" />
                    <span class="strength-text ml-2" :class="strengthTextClass">{{ strengthText }}</span>
                  </div>

                  <!-- List of password requirements with checkmarks/crosses -->
                  <div class="requirements-list">
                    <div v-for="(requirement, index) in passwordRequirementsDetails" :key="index"
                         class="requirement-item"
                         :class="{ 'text-success': requirement.met, 'text-danger': !requirement.met }">
                      <feather-icon :icon="requirement.met ? 'CheckCircleIcon' : 'XCircleIcon'" size="16" />
                      <span class="font-weight-bold ml-2">{{ requirement.text }}</span>
                    </div>
                  </div>
                </div>

                <b-form-group id="input-group-10" label="Confirmez le mot de passe" label-for="input-10">
                  <b-form-input id="input-10" v-model="form.confirmationPassword" type="password"
                                placeholder="Confirmez votre mot de passe" required @input="validatePassword" />
                </b-form-group>
                <!-- Password match indicator -->
                <div v-if="form.password && form.confirmationPassword" class="password-match mb-3"
                     :class="passwordsMatch ? 'text-success' : 'text-danger'">
                  <feather-icon :icon="passwordsMatch ? 'CheckCircleIcon' : 'XCircleIcon'" size="16" />
                  <span class="font-weight-bold ml-2">{{ passwordsMatch ? 'Les mots de passe correspondent' : 'Les mots de passe ne correspondent pas' }}</span>
                </div>
              </template>
            </template>

            <!-- Error messages -->
            <div v-if="errorMessages.length" class="error-message text-danger text-center mt-2">
              <div v-for="(message, index) in errorMessages" :key="index">
                {{ message }}
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Status checkboxes -->
        <b-row class="form__row">
          <b-col :md="4" class="d-flex">
            <b-form-group v-if="mode !== 'reset'" id="status-group" label="Status" label-for="status">
              <b-form-checkbox id="status" v-model="form.status" name="check-button" switch
                               :disabled="mode === 'profile'" />
            </b-form-group>

            <b-form-group v-if="showCanSendMessage" id="can-send-message-group" label="Messages" label-for="CanSendMessage" class="ml-3">
              <b-form-checkbox v-if="mode === 'modifier'" id="CanSendMessage" v-model="form.profile.canSendMessage" name="check-button" switch
                               :disabled="mode === 'profile'" />
              <b-form-checkbox v-else id="CanSendMessage" v-model="form.canSendMessage" name="check-button" switch
                               :disabled="mode === 'profile'" />
            </b-form-group>

            <b-form-group v-if="showBackupOption" id="backup-group" label="Backup" label-for="backup" class="ml-3">
              <b-form-checkbox id="backup" v-model="form.profile.isBackup" name="check-button" switch
                               :disabled="mode === 'profile'" />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Action buttons -->
        <b-row class="form__row form__actions">
          <b-button v-if="mode === 'ajouter'" type="submit" variant="primary" :disabled="!isValid">
            Ajouter
          </b-button>
          <b-button v-if="mode === 'modifier' || showPasswordFields" type="submit" variant="primary"
                    :disabled="showPasswordFields && !isValid">
            Modifier
          </b-button>
          <b-button v-if="mode === 'profile' && showPasswordFields" class="cancel ml-2" variant="secondary"
                    @click="showPasswordFields = false">
            Annuler
          </b-button>
          <b-button v-if="mode === 'profile' && !showPasswordFields" variant="primary"
                    @click="showPasswordFields = true; scrollToBottom()">
            Modifier mon mot de passe
          </b-button>
          <b-button v-if="mode === 'reset' && !showPasswordFields" variant="primary"
                    @click="showPasswordFields = true; scrollToBottom()">
            Réinitialiser le mot de passe
          </b-button>
        </b-row>
      </b-container>
    </b-form>
  </div>
</template>

<script>
import {
  BContainer,
  BCol,
  BRow,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BButton,
  BFormSelect,
  BProgress,
} from 'bootstrap-vue'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ref, computed } from '@vue/composition-api'
import zxcvbn from 'zxcvbn'
import router from '@/router'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export default {
  components: {
    BContainer,
    BCol,
    BRow,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BButton,
    BFormSelect,
    BProgress,
  },
  props: {
    userData: {
      default: () => ({}),
      type: Object,
      required: false,
    },
    email: {
      default: '',
      type: String,
      required: false,
    },
    phone: {
      default: '',
      type: String,
      required: false,
    },
    mode: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const form = ref({
      ...props.userData,
      profile: {
        ...(props.userData.profile || {}),
      },
      password: '',
      confirmationPassword: '',
      currentPassword: '',
    })

    const roles = ref('')
    const showPasswordFields = ref(false)
    const errorMessages = ref([])
    const passwordStrength = ref({ score: 0 })
    const toast = useToast()

    const displayToast = (title, icon, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          position: 'bottom-right',
          icon,
          variant,
        },
      })
    }

    const translateApiErrors = apiErrors => {
      const translations = {
        PasswordTooShort: 'Les mots de passe doivent contenir au moins 8 caractères.',
        PasswordRequiresDigit: "Les mots de passe doivent contenir au moins un chiffre ('0'-'9').",
        PasswordRequiresUpper: "Les mots de passe doivent contenir au moins une lettre majuscule ('A'-'Z').",
        PasswordRequiresNonAlphanumeric: 'Les mots de passe doivent contenir au moins un caractère non alphanumérique.',
      }

      return Object.keys(apiErrors)
        .filter(key => translations[key])
        .map(key => translations[key])
    }

    const handleApiError = (error, context) => {
      if (!error.response) {
        displayToast('Une erreur réseau s\'est produite', 'WifiOffIcon', 'danger')
        return
      }

      if (error.response.status === 409) {
        displayToast(
          'Il semble que ces informations ne puissent pas être utilisées. Merci d\'en essayer d\'autres.',
          'XCircleIcon',
          'danger',
        )
        return
      }

      if (error.response.status === 400 && error.response.data) {
        errorMessages.value = translateApiErrors(error.response.data)
        return
      }

      const contextMessages = {
        create: 'Impossible de créer l\'utilisateur',
        update: 'Impossible de modifier l\'utilisateur',
        account: 'Impossible de mettre à jour l\'utilisateur',
      }

      const key = context === 'password' ? 'account' : context

      displayToast(
        contextMessages[key] || 'Une erreur est survenue',
        'XCircleIcon',
        'danger',
      )
    }

    // Password requirements array [minLength, hasUpper, hasLower, hasDigit, hasSpecial]
    const passwordRequirements = ref([false, false, false, false, false])

    const passwordRequirementsDetails = computed(() => [
      {
        met: passwordRequirements.value[0],
        text: 'Le mot de passe doit contenir au moins 8 caractères.',
      },
      {
        met: passwordRequirements.value[1],
        text: 'Le mot de passe doit contenir au moins une lettre majuscule (A-Z).',
      },
      {
        met: passwordRequirements.value[2],
        text: 'Le mot de passe doit contenir au moins une lettre minuscule (a-z).',
      },
      {
        met: passwordRequirements.value[3],
        text: 'Le mot de passe doit contenir au moins un chiffre (0-9).',
      },
      {
        met: passwordRequirements.value[4],
        text: 'Le mot de passe doit contenir au moins un caractère spécial (#$@!&*...).',
      },
    ])

    const passwordsMatch = computed(() => form.value.password === form.value.confirmationPassword && form.value.password.length > 0 && form.value.confirmationPassword.length > 0)

    const handlePasswordReset = () => {
      const forceResetPasswordBody = {
        newPassword: form.value.password,
        confirmNewPassword: form.value.confirmationPassword,
        userId: form.value.id,
      }

      store.dispatch('users/forceResetPassword', forceResetPasswordBody)
        .then(() => {
          displayToast('Mot de passe mis à jour', 'CheckIcon', 'success')
          emit('submit', true)
        })
        .catch(error => handleApiError(error, 'password'))
    }

    const handleProfileUpdate = () => {
      const resetPasswordBody = {
        currentPassword: form.value.currentPassword,
        newPassword: form.value.password,
        confirmNewPassword: form.value.confirmationPassword,
        userId: form.value.id,
      }

      store.dispatch('users/resetPassword', resetPasswordBody)
        .then(() => {
          displayToast('Mot de passe mis à jour', 'CheckIcon', 'success')
          // Redirect logic
          setTimeout(() => {
            displayToast('Vous allez être redirigé', 'AlertTriangleIcon', 'warning')
          }, 500)

          setTimeout(() => {
            toast.clear()
            router.push({ name: 'login' }).then(() => {
              displayToast('Veuillez saisir votre nouveau mot de passe', 'InfoIcon', 'info')
            }).catch(() => { })
          }, 2700) // Redirection timer
          emit('submit', true)
        })
        .catch(error => handleApiError(error, 'password'))
    }

    const handleUpdateUser = () => {
      const updatedUser = {
        firstName: form.value.prenom,
        lastName: form.value.nom.toUpperCase(),
        profile: {
          department: form.value.departement,
          isActive: form.value.status,
          isBackup: form.value.profile.isBackup,
          callRecording: form.value.profile.callRecording,
          canSendMessage: form.value.profile.canSendMessage,
        },
        roles: form.value.roles,
        email: form.value.email,
        emailConfirmed: props.email === form.value.email,
        phoneNumberConfirmed: props.phone === form.value.telephone,
        phoneNumber: form.value.telephone,
        userName: form.value.identifiant,
        id: form.value.id,
      }

      // Reset backup for non-eligible roles
      if (
        updatedUser.roles[0] !== 'LeRefuge.Ecoutant'
        && updatedUser.roles[0] !== 'LeRefuge.Coordinateur'
      ) {
        updatedUser.profile.isBackup = false
      }

      store.dispatch('users/updateUser', updatedUser)
        .then(() => {
          displayToast('Utilisateur modifié', 'CheckIcon', 'success')
          store.dispatch('users/fetchUsers')
          emit('submit', true)
        })
        .catch(error => handleApiError(error, 'update'))
    }

    const handleAddUser = () => {
      const userObj = {
        firstName: form.value.prenom,
        lastName: form.value.nom.toUpperCase(),
        profile: {
          department: form.value.departement,
          isActive: form.value.status,
          callRecording: form.value.callRecording,
          canSendMessage: form.value.canSendMessage ?? false,
        },
        roles: [roles.value],
        email: form.value.email,
        emailConfirmed: false,
        phoneNumberConfirmed: false,
        phoneNumber: form.value.telephone,
        userName: form.value.identifiant,
      }

      store.dispatch('auth/register', userObj)
        .then(() => {
          displayToast('Utilisateur ajouté', 'CheckIcon', 'success')
          emit('submit', true)
        })
        .catch(error => handleApiError(error, 'create'))
    }

    const validatePassword = () => {
      const { password } = form.value

      passwordRequirements.value = [
        password.length >= 8,
        /[A-Z]/.test(password),
        /[a-z]/.test(password),
        /\d/.test(password),
        /[^A-Za-z0-9]/.test(password),
      ]

      // Get zxcvbn score if password exists
      if (password) {
        passwordStrength.value = zxcvbn(password)
      } else {
        passwordStrength.value = { score: 0 }
      }
    }

    const isValid = computed(() => {
      // For password forms, check all requirements and matching passwords
      if (showPasswordFields.value || props.mode === 'reset') {
        const allRequirementsMet = passwordRequirements.value.every(req => req)
        return allRequirementsMet && passwordsMatch.value && form.value.password.length > 0
      }
      return true
    })

    // --- Submit form ---
    const onSubmit = mode => {
      if ((showPasswordFields.value || mode === 'reset') && !isValid.value) {
        displayToast(
          'Votre mot de passe ne répond pas aux exigences de sécurité',
          'XCircleIcon',
          'danger',
        )
        return
      }

      // Check password matching
      if (
        (showPasswordFields.value || mode === 'reset')
        && form.value.password !== form.value.confirmationPassword
      ) {
        displayToast(
          'Les mots de passe que vous avez saisis ne correspondent pas',
          'XCircleIcon',
          'danger',
        )
        return
      }

      // Check if new password is same as current (only for profile mode)
      if (mode === 'profile' && form.value.currentPassword === form.value.password) {
        displayToast(
          'Le nouveau mot de passe doit être différent du mot de passe actuel',
          'XCircleIcon',
          'danger',
        )
        return
      }

      if (mode === 'ajouter') {
        handleAddUser()
      } else if (mode === 'modifier') {
        handleUpdateUser()
      } else if (mode === 'profile') {
        handleProfileUpdate()
      } else if (mode === 'reset') {
        handlePasswordReset()
      }
    }

    const scrollToBottom = () => {
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          left: 0,
          behavior: 'smooth',
        })
      }, 250)
    }

    const formTitle = computed(() => {
      if (props.mode === 'profile') { return 'Profil utilisateur' }
      if (props.mode === 'reset') { return 'Réinitialisation de mot de passe utilisateur' }
      return 'Fiche utilisateur'
    })

    const eligibleRoles = ['LeRefuge.Ecoutant', 'LeRefuge.Coordinateur']
    const showCanSendMessage = computed(() => {
      if (props.mode === 'modifier' || props.mode === 'profile') {
        return eligibleRoles.includes(form.value.roles[0])
      }

      return props.mode === 'ajouter'
    })
    const showBackupOption = computed(() => props.mode === 'modifier' && eligibleRoles.includes(form.value.roles[0]))

    const passwordCompliance = computed(() => {
      const metCount = passwordRequirements.value.filter(req => req).length
      return (metCount / passwordRequirements.value.length) * 100
    })

    const strengthText = computed(() => {
      const { score } = passwordStrength.value
      const labels = ['Très faible', 'Faible', 'Moyen', 'Fort', 'Très fort']
      return labels[score] || labels[0]
    })

    const strengthVariant = computed(() => {
      const metCount = passwordRequirements.value.filter(req => req).length
      if (metCount === 0) { return 'danger' }
      if (metCount < 3) { return 'warning' }
      if (metCount < 5) { return 'info' }
      return 'success'
    })

    const strengthTextClass = computed(() => {
      const variants = {
        danger: 'text-danger',
        warning: 'text-warning',
        info: 'text-info',
        success: 'text-success',
      }
      return variants[strengthVariant.value] || 'text-muted'
    })

    const roleList = [
      { value: 'LeRefuge.Administrateur', text: 'Administrateur' },
      { value: 'LeRefuge.Coordinateur', text: 'Coordinateur' },
      { value: 'LeRefuge.Ecoutant', text: 'Ecoutant' },
      { value: 'LeRefuge.SuperAdmin', text: 'SuperAdmin' },
    ]

    const callRecording = [
      { value: true, text: 'Oui' },
      { value: false, text: 'Non' },
    ]

    return {
      router,
      form,
      roles,
      roleList,
      callRecording,
      showPasswordFields,
      errorMessages,
      passwordStrength,
      passwordRequirements,
      formTitle,
      showCanSendMessage,
      showBackupOption,
      passwordCompliance,
      strengthText,
      strengthVariant,
      strengthTextClass,
      isValid,
      onSubmit,
      scrollToBottom,
      passwordRequirementsDetails,
      passwordsMatch,
      validatePassword,
      FeatherIcon,
    }
  },
}
</script>

<style lang="scss" scoped>
.form {
  padding-bottom: 1.5rem;

  &__title {
    font-size: 24px;
    display: flex;
    justify-content: center;
    margin: 2rem 0;
    color: #16B5EA;
    font-weight: 700;
  }

  &__row {
    justify-content: center;
    margin-bottom: 1rem;

    > .col-md-4 {
      padding-right: 0.75rem;
      padding-left: 0.75rem;
    }
  }

  &__actions {
    margin-top: 1.5rem;
    margin-bottom: 0;
  }
}

.strength-text {
  font-weight: 800;
  font-size: 0.90rem;
}

.error-message {
  font-size: 0.9rem;
  font-weight: 800;
  color: red;
}

.required-marker {
  color: #dc3545;
  font-weight: 800;
}

.activation-notice {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  max-width: 100%;
  margin: 0.25rem 0 1.25rem;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(22, 181, 234, 0.35);
  border-radius: 0.35rem;
  background: rgba(22, 181, 234, 0.12);
  color: #d9f6ff;
}

.activation-notice__icon {
  flex: 0 0 auto;
  margin-top: 0.1rem;
  color: #16b5ea;
}

.activation-notice__title,
.activation-notice__message {
  margin: 0;
}

.activation-notice__title {
  font-weight: 700;
}

.activation-notice__message {
  margin-top: 0.2rem;
  color: #b8c8df;
  line-height: 1.45;
}

.password-requirements,
.password-match {
  font-size: 0.85rem;
  transition: all 0.3s ease;
}

.requirements-list {
  margin-top: 8px;
}

.requirement-item {
  margin-bottom: 0.80rem;
  display: flex;
  align-items: center;
}

svg.feather {
  min-width: 16px;
  min-height: 16px;
}
</style>
